/**
 * NEXUMA — Context-aware page tour
 *
 * Builds a tour for whatever the user is currently looking at: if a dialog/form
 * is open it walks through that form's fields, otherwise it walks through the
 * page's controls AND content blocks (tabs, search, fields, toggles, buttons,
 * links, chips, icon buttons, visual cards/tiles and tables) in visual order.
 *
 * Detection is intentionally generous so custom (non-MUI-button) elements such
 * as org-chart cards, stat tiles and clickable rows are also covered.
 *
 * Steps reference live DOM nodes (step.el) captured at build time, so this must
 * be called the moment the user clicks the help (❓) button.
 */

const MAX_STEPS = 60;
const MAX_CARD_W = 560;   // wider than this → a page section, not an item
const MAX_CARD_H = 640;
const MIN_CARD_W = 64;
const MIN_CARD_H = 28;

function isVisible(el) {
  if (!el) return false;
  const r = el.getBoundingClientRect();
  if (r.width < 6 || r.height < 6) return false;       // display:none ancestor → 0 size
  if (r.bottom < 0 || r.right < 0) return false;
  const cs = window.getComputedStyle(el);
  return cs.visibility !== 'hidden' && cs.display !== 'none' && cs.opacity !== '0';
}

function clean(s) {
  return (s || '').replace(/\s+/g, ' ').replace(/\s*\*\s*$/, '').trim();
}

function labelFor(ctrl) {
  const lab = ctrl.querySelector('label, .MuiFormLabel-root, legend');
  let text = lab ? lab.textContent : '';
  if (!text) {
    const inp = ctrl.querySelector('input, textarea, select');
    text = (inp && (inp.getAttribute('aria-label') || inp.getAttribute('placeholder'))) || '';
  }
  return clean(text);
}

// Join an element's visible text with spaces so sibling Typography nodes that
// have no whitespace between them in the DOM (e.g. a "2" next to "Gjithsej")
// don't get mashed into "2Gjithsej".
function spacedText(el) {
  const parts = [];
  el.querySelectorAll('h1,h2,h3,h4,h5,h6,strong,p,span,div,.MuiTypography-root').forEach((n) => {
    if (n.children.length === 0) { const tx = clean(n.textContent); if (tx) parts.push(tx); }
  });
  const joined = parts.length ? Array.from(new Set(parts)).join(' ') : clean(el.textContent);
  return clean(joined);
}

function headingText(el) {
  const h = el.querySelector('h1,h2,h3,h4,h5,h6,strong,.MuiTypography-h6,.MuiTypography-subtitle1,.MuiTypography-subtitle2');
  return (h ? clean(h.textContent) : spacedText(el)).slice(0, 48);
}

// Does this element look like a self-contained card / tile / clickable item?
function looksLikeCard(el) {
  const r = el.getBoundingClientRect();
  if (r.width < MIN_CARD_W || r.width > MAX_CARD_W) return false;
  if (r.height < MIN_CARD_H || r.height > MAX_CARD_H) return false;
  if (!clean(el.textContent)) return false;
  const cs = window.getComputedStyle(el);
  if (cs.visibility === 'hidden' || cs.display === 'none') return false;
  if ((parseFloat(cs.borderRadius) || 0) < 2 && cs.cursor !== 'pointer') return false;
  const hasBorder =
    parseFloat(cs.borderTopWidth) >= 1 || parseFloat(cs.borderBottomWidth) >= 1 ||
    parseFloat(cs.borderLeftWidth) >= 1 || parseFloat(cs.borderRightWidth) >= 1;
  const hasShadow = cs.boxShadow && cs.boxShadow !== 'none';
  return hasBorder || hasShadow || cs.cursor === 'pointer';
}

export function buildPageTour(t) {
  // ── Scope: an open dialog takes priority over the page content ──────────────
  const dialog = Array.from(document.querySelectorAll('[role="dialog"]')).find(isVisible);
  const scope = dialog || document.querySelector('[data-tour="content"]') || document.body;

  const steps = [];
  steps.push({
    id: 'page_intro',
    title: dialog ? t('tour.dyn.formTitle') : t('tour.dyn.pageTitle'),
    body: dialog ? t('tour.dyn.formBody') : t('tour.dyn.pageBody'),
    el: scope,
    placement: 'page',
  });

  const picked = [];
  const cands = [];
  const add = (el, kind, label) => {
    if (!el || !isVisible(el)) return;
    if (picked.some((p) => p === el || p.contains(el) || el.contains(p))) return;
    picked.push(el);
    cands.push({ el, kind, label });
  };

  // 1) Tabs (one step for the whole tablist)
  const tablist = scope.querySelector('[role="tablist"]');
  if (tablist) add(tablist, 'tabs');

  // 2) Search inputs
  scope.querySelectorAll('input[type="search"], input[placeholder]').forEach((inp) => {
    const ph = (inp.getAttribute('placeholder') || '').toLowerCase();
    if (!/kërko|kerko|search|suche|filtro|filter/.test(ph)) return;
    add(inp.closest('.MuiFormControl-root, .MuiTextField-root') || inp, 'search');
  });

  // 3) Form fields (top-level MUI controls with a label)
  scope.querySelectorAll('.MuiFormControl-root, .MuiTextField-root').forEach((fc) => {
    if (fc.parentElement && fc.parentElement.closest('.MuiFormControl-root')) return;
    const label = labelFor(fc);
    if (label) add(fc, 'field', label);
  });

  // 4) Toggles
  scope.querySelectorAll('.MuiSwitch-root, .MuiCheckbox-root, .MuiRadio-root').forEach((sw) => {
    const wrap = sw.closest('.MuiFormControlLabel-root') || sw;
    add(wrap, 'toggle', clean(wrap.textContent));
  });

  // 5) Buttons with text (explicit actions)
  scope.querySelectorAll('.MuiButton-root, button').forEach((btn) => {
    const txt = clean(btn.textContent);
    if (txt && txt.length <= 40) add(btn, 'button', txt);
  });

  // 6) Visual cards / tiles / clickable items — inner (smaller) ones win
  const visualCards = [];
  const all = scope.querySelectorAll('*');
  for (let i = 0; i < all.length && i < 6000; i += 1) {
    if (looksLikeCard(all[i])) visualCards.push(all[i]);
  }
  visualCards.sort((a, b) => {
    const ra = a.getBoundingClientRect();
    const rb = b.getBoundingClientRect();
    return (ra.width * ra.height) - (rb.width * rb.height);
  });
  visualCards.forEach((el) => add(el, 'card', headingText(el)));

  // 7) Links with text
  scope.querySelectorAll('a[href]').forEach((a) => {
    const txt = clean(a.textContent);
    if (txt && txt.length <= 40) add(a, 'link', txt);
  });

  // 8) Chips (informational badges)
  scope.querySelectorAll('.MuiChip-root').forEach((chip) => {
    add(chip, 'chip', clean(chip.textContent));
  });

  // 9) Icon buttons with an accessible label
  scope.querySelectorAll('.MuiIconButton-root').forEach((ib) => {
    const label = clean(ib.getAttribute('aria-label') || ib.getAttribute('title'));
    if (label) add(ib, 'iconButton', label);
  });

  // 10) Tables (one step explaining the rows)
  const table = scope.querySelector('.MuiTable-root, table');
  if (table) add(table, 'table');

  // Order by visual position (top → bottom, left → right)
  cands.sort((a, b) => {
    const ra = a.el.getBoundingClientRect();
    const rb = b.el.getBoundingClientRect();
    return (Math.round(ra.top) - Math.round(rb.top)) || (ra.left - rb.left);
  });

  cands.slice(0, MAX_STEPS).forEach((c, i) => {
    let title;
    let body;
    switch (c.kind) {
      case 'tabs':   title = t('tour.dyn.tabsTitle');   body = t('tour.dyn.tabs'); break;
      case 'search': title = t('tour.dyn.searchTitle'); body = t('tour.dyn.search'); break;
      case 'field':  title = c.label;                   body = t('tour.dyn.field'); break;
      case 'toggle': title = c.label || t('tour.dyn.toggleTitle'); body = t('tour.dyn.toggle'); break;
      case 'card':   title = c.label || t('tour.dyn.itemTitle');   body = t('tour.dyn.item'); break;
      case 'link':   title = c.label; body = t('tour.dyn.link'); break;
      case 'chip':   title = c.label || t('tour.dyn.chipTitle'); body = t('tour.dyn.chip'); break;
      case 'iconButton': title = c.label; body = t('tour.dyn.iconButton'); break;
      case 'table':  title = t('tour.dyn.tableTitle'); body = t('tour.dyn.table'); break;
      default:       title = c.label; body = t('tour.dyn.button'); break;
    }
    // Demo mode: auto-fill plain form fields with sample text (safe — never saves).
    const action = c.kind === 'field' ? 'fill' : undefined;
    steps.push({ id: `dyn_${i}`, title, body, el: c.el, placement: 'auto', skipIfMissing: true, action });
  });

  if (cands.length === 0) {
    steps.push({
      id: 'page_empty',
      title: t('tour.dyn.emptyTitle'),
      body: t('tour.dyn.empty'),
      el: scope,
      placement: 'page',
    });
  }

  steps.push({ id: 'page_done', title: t('tour.dyn.doneTitle'), body: t('tour.dyn.done'), target: null });
  return steps;
}

export default buildPageTour;
